import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Copy, Share2, Mail, Plus, Trash, ExternalLink, LogOut } from 'lucide-react';
import BrandHeader from '../components/BrandHeader';
import BroadcastCard from '../components/BroadcastCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TF_LOGO = 'https://www.targetfunder.com/tf_logo.png';
// Prefix the backend URL onto relative upload paths (/api/uploads/...) so the
// admin page works on production where frontend + backend are on different
// domains. Returns absolute URLs unchanged.
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;

/**
 * Single-page club admin. No tabs, no sidebar.
 * Sections (top to bottom): Header (logo + LIVE) → Share → Stats + Entries → Prizes → Footer.
 * Access model: magic-link token in ?magic=... or already-stored token in localStorage.
 */
export default function ClubAdminPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(() => localStorage.getItem(`club_admin_${slug}`) || '');
  const [club, setClub] = useState(null);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total_raised: 0, total_entries: 0 });
  const [loading, setLoading] = useState(true);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [accessError, setAccessError] = useState('');

  // One-shot: accept magic token from URL, store, strip query param
  useEffect(() => {
    const magic = searchParams.get('magic');
    if (magic) {
      localStorage.setItem(`club_admin_${slug}`, magic);
      setToken(magic);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams, slug]);

  const headers = token ? { headers: { Authorization: `Bearer ${token}` } } : null;

  // Super admin password (persisted in localStorage from SuperAdminPage login).
  // When present, the entries table shows a "Resend confirmation" button per
  // row that calls the super-admin-only /admin/entries/:id/resend-confirmation
  // endpoint. This lets support re-fire confirmations directly from the club's
  // dashboard (opened via "Open Club Dashboard" impersonation) without going
  // back to the Super Admin lookup card.
  const superAdminPw = typeof window !== 'undefined' ? localStorage.getItem('tf_super_admin_pw') : null;

  // Sport is derived from the URL base path (/worldcup2026 | /opengolf2026 | /allireland2026)
  const basePath = window.location.pathname.split('/')[1] || 'worldcup2026';
  const sport = basePath === 'allireland2026' ? 'gaa' : basePath === 'opengolf2026' ? 'golf' : 'wc';
  const predictorType = sport === 'gaa' ? 'gaa_final_2026' : sport === 'golf' ? 'golf_open_2026' : 'wc_2026';

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    const load = async () => {
      try {
        const clubEndpoint = sport === 'gaa'
          ? `${API}/gaa/clubs/${slug}`
          : sport === 'golf'
            ? `${API}/golf/clubs/${slug}`
            : `${API}/clubs/${slug}`;
        const [clubRes, entriesRes, statsRes] = await Promise.all([
          axios.get(clubEndpoint),
          axios.get(`${API}/clubs/${slug}/entries?status=paid&predictor_type=${predictorType}`).catch(() => ({ data: { entries: [] } })),
          axios.get(`${API}/clubs/${slug}/stats?predictor_type=${predictorType}`).catch(() => ({ data: {} })),
        ]);
        setClub(clubRes.data);
        setEntries(entriesRes.data.entries || []);
        setStats({
          total_raised: statsRes.data.total_raised ?? 0,
          total_entries: statsRes.data.total_entries ?? (entriesRes.data.entries || []).length,
          // paid_entries drives the dashboard "Entries" tile so the count
          // always matches Raised. Fallback to entries.length (the table is
          // already filtered to paid-only) if the API stat is missing.
          paid_entries: statsRes.data.paid_entries ?? (entriesRes.data.entries || []).length,
        });
        setAccessError('');
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem(`club_admin_${slug}`);
          setToken('');
          setAccessError('Your access link has expired. Enter your email below to get a new one.');
        } else {
          setAccessError(err.response?.data?.detail || 'Could not load your dashboard.');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, slug, sport]);

  const requestMagic = async (e) => {
    e?.preventDefault?.();
    if (!magicEmail.trim()) return;
    try {
      await axios.post(`${API}/clubs/${slug}/magic-link?predictor_type=${predictorType}`, { email: magicEmail.trim() });
      setMagicSent(true);
    } catch {
      setMagicSent(true); // still show success — don't leak email existence
    }
  };

  const logout = () => {
    localStorage.removeItem(`club_admin_${slug}`);
    setToken('');
  };

  if (loading) return <LoadingScreen />;
  if (!token) return <MagicLinkScreen slug={slug} magicEmail={magicEmail} setMagicEmail={setMagicEmail} magicSent={magicSent} onSubmit={requestMagic} />;
  if (accessError) return <ErrorScreen message={accessError} />;
  if (!club) return null;

  const currencySym = { GBP: '£', EUR: '€', USD: '$' }[club.currency] || '';
  const clubUrl = `${window.location.origin}/${basePath}/${slug}`;
  const primary = club.primary_color || '#1D3557';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {club.logo_url ? (
              <img src={assetUrl(club.logo_url)} alt={club.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex-shrink-0" style={{ background: primary }} />
            )}
            <div className="min-w-0">
              <p className="text-sm font-black text-gray-900 truncate">{club.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`w-2 h-2 rounded-full ${club.is_live ? 'bg-green-500' : 'bg-amber-500'}`} />
                <span className="text-xs text-gray-600">{club.is_live ? 'Live' : 'Not live yet'}</span>
              </div>
            </div>
          </div>
          <button onClick={logout} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1.5" data-testid="logout-btn">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">

        {/* Share */}
        <ShareCard clubUrl={clubUrl} club={club} fee={club.entry_fee} currencySym={currencySym} primary={primary} sport={sport} />

        {/* Broadcast leaderboard email — WC only, live clubs only */}
        {club.is_live && sport === 'wc' && (
          <BroadcastCard slug={slug} primary={primary} authHeaders={headers} />
        )}

        {/* Stats + Entries */}
        <EntriesCard entries={entries} stats={stats} currencySym={currencySym} primary={primary} pricingModel={club.pricing_model} superAdminPw={superAdminPw} />

        {/* GAA insights — % Kerry / Mayo / Draw + avg score + top scorelines,
            with a one-click "Copy for social" caption. GAA-only for now. */}
        {sport === 'gaa' && (
          <GaaInsightsCard slug={slug} club={club} primary={primary} authHeaders={headers} />
        )}

        {/* Prizes */}
        {club.is_live || (club.prizes || []).length > 0 ? (
          <PrizesCard slug={slug} initial={club.prizes || []} primary={primary} authHeaders={headers} onSaved={p => setClub({ ...club, prizes: p })} />
        ) : null}

        {/* Footer */}
        <div className="pt-4 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm">
          <a
            href={`${API}/clubs/${slug}/stripe-connect/dashboard`}
            onClick={async (e) => {
              e.preventDefault();
              try {
                const r = await axios.post(`${API}/clubs/${slug}/stripe-connect/dashboard`, {}, headers);
                if (r.data.dashboard_url) window.open(r.data.dashboard_url, '_blank', 'noopener');
              } catch { /* ignore */ }
            }}
            className="inline-flex items-center gap-1.5 text-[#635BFF] font-medium hover:underline"
            data-testid="stripe-dashboard-link"
          >
            <ExternalLink size={14} /> Open Stripe dashboard
          </a>
          <span className="text-xs text-gray-500">
            Need help? <a href="mailto:support@targetfunder.com" className="underline">support@targetfunder.com</a>
          </span>
        </div>
        {/* Powered by TargetFunder */}
        <div className="pt-6 pb-10 flex items-center justify-center gap-2 border-t border-gray-200" data-testid="tf-footer-brand">
          <span className="text-xs uppercase tracking-wider text-gray-400">Powered by</span>
          <a href="https://www.targetfunder.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src={TF_LOGO} alt="TargetFunder" className="h-8 w-auto object-contain" />
            <span className="font-heading font-black text-xl uppercase tracking-tighter leading-none text-[#1D3557]">
              target<span className="text-[#E63946]">funder</span><span className="text-[#1D3557]/30">.com</span>
            </span>
          </a>
        </div>
      </main>
    </div>
  );
}

// ----- Sub-components -----

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#1D3557]/20 border-t-[#1D3557] rounded-full animate-spin" />
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <p className="text-gray-700 mb-3">{message}</p>
        <a href="mailto:support@targetfunder.com" className="text-[#1D3557] font-semibold underline">Contact support</a>
      </div>
    </div>
  );
}

function MagicLinkScreen({ slug, magicEmail, setMagicEmail, magicSent, onSubmit }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          {magicSent ? (
            <div className="text-center" data-testid="magic-sent">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Mail size={28} className="text-green-600" />
              </div>
              <h1 className="text-2xl font-black text-[#1D3557] mb-2">Check your email</h1>
              <p className="text-gray-600">If <strong>{magicEmail}</strong> is on file for this club, we've sent a one-click dashboard link.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="text-center" data-testid="magic-form">
              <h1 className="text-2xl font-black text-[#1D3557] mb-2">Dashboard access</h1>
              <p className="text-gray-600 mb-6">Enter the email you used to sign up, and we'll email a one-click link to your {slug} dashboard.</p>
              <input
                type="email"
                required
                value={magicEmail}
                onChange={e => setMagicEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg mb-3"
                data-testid="magic-email"
              />
              <button type="submit" className="w-full py-3 bg-[#1D3557] hover:bg-[#15293f] text-white font-bold rounded-lg" data-testid="magic-send">
                Email me a dashboard link
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function ShareCard({ clubUrl, club, fee, currencySym, primary, sport }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(clubUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const isGaa = sport === 'gaa';
  const isGolf = sport === 'golf';
  const productName = isGaa ? 'All-Ireland Final Predictor' : isGolf ? 'Open Golf 2026 Predictor' : 'World Cup 2026 Predictor';
  const pitch = isGaa
    ? 'Predict the exact Kerry v Mayo final score'
    : isGolf
      ? 'Pick your 4 golfers'
      : 'Pick your 6 teams';
  const followLine = isGaa
    ? 'Predict the exact final score — Kerry v Mayo, goals and points. Exact score wins outright; closest score counts if nobody nails it.'
    : isGolf
      ? `Pick 4 golfers, pay ${currencySym}${fee?.toFixed(2)} entry, and follow the leaderboard through The Open.`
      : `Pick 6 teams, pay ${currencySym}${fee?.toFixed(2)} entry, and follow the leaderboard as teams progress.`;
  const waMsg = `🏆 ${club.name}'s ${productName} is live! ${pitch}, pay ${currencySym}${fee?.toFixed(2)}, and battle it out for the top spot. Every entry supports the club. ${clubUrl}`;
  const emailSubj = `${club.name} ${productName}`;
  const emailBody = `${club.name} is running a ${productName} — a fun way to support the club.\n\n${followLine}\n\nEnter here: ${clubUrl}`;

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="share-card">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Share your predictor</p>
      <div className="flex gap-2 mb-3">
        <input value={clubUrl} readOnly className="flex-1 px-3 py-2.5 font-mono text-sm bg-gray-50 border border-gray-200 rounded-lg" data-testid="share-url" />
        <button onClick={copy} className="px-4 py-2.5 text-white font-bold text-sm rounded-lg flex items-center gap-2" style={{ background: primary }} data-testid="copy-url">
          <Copy size={14} /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <a href={`https://wa.me/?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noopener noreferrer" className="py-2.5 bg-[#25D366] text-white font-bold text-sm rounded-lg flex items-center justify-center gap-2 hover:bg-[#1EBE56]" data-testid="share-whatsapp">
          <Share2 size={14} /> WhatsApp
        </a>
        <a href={`mailto:?subject=${encodeURIComponent(emailSubj)}&body=${encodeURIComponent(emailBody)}`} className="py-2.5 bg-[#1D3557] text-white font-bold text-sm rounded-lg flex items-center justify-center gap-2 hover:bg-[#15293f]" data-testid="share-email">
          <Mail size={14} /> Email
        </a>
      </div>
    </section>
  );
}

function EntriesCard({ entries, stats, currencySym, primary, pricingModel, superAdminPw }) {
  // "Raised" is the gross customer payments. Net to the club is lower because:
  //   • setup_fee model: Stripe processing fee (~£0.35-£0.55 / €0.40) is deducted on Stripe's side
  //     before funds settle into the club's connected account.
  //   • per_entry model: 99p/99c platform fee + Stripe fee are deducted before the transfer
  //     to the club's account.
  // Either way, the dashboard can't compute the exact net (depends on card type, FX,
  // and Stripe's variable rates), so the truth lives in their Stripe dashboard.
  const isPerEntry = pricingModel === 'per_entry';
  const netNote = isPerEntry
    ? `Gross customer payments — TargetFunder takes a 99c/99p platform fee per entry (which includes Stripe processing fees). Your Stripe dashboard shows the exact net deposited into your bank.`
    : `Gross customer payments — Stripe's processing fee (typically ~3%) is deducted before funds settle into your account. Your Stripe dashboard shows the exact net.`;

  // Per-row resend state (super admin only): tracks which row is in-flight
  // + last result banner. Two actions: normal resend (skips if already logged)
  // and force resend (bypasses duplicate check — for "in spam" cases).
  const isSuperAdmin = Boolean(superAdminPw);
  const [busyId, setBusyId] = useState(null);
  const [resendMsg, setResendMsg] = useState(null);

  const resendConfirmation = async (entry, force) => {
    const label = force ? 'FORCE re-send (bypass duplicate check)' : 'Re-send confirmation email';
    if (!window.confirm(`${label} to ${entry.player_email}?`)) return;
    setBusyId(entry.id); setResendMsg(null);
    try {
      const r = await axios.post(
        `${API}/admin/entries/${entry.id}/resend-confirmation`,
        {},
        {
          auth: { username: 'admin', password: superAdminPw },
          params: force ? { force: true } : {},
        },
      );
      setResendMsg({ id: entry.id, text: r.data.message, ok: r.data.sent !== false });
    } catch (err) {
      setResendMsg({ id: entry.id, text: err.response?.data?.detail || 'Failed', ok: false });
    } finally {
      setBusyId(null);
      setTimeout(() => setResendMsg(null), 6000);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="entries-card">
      <div className="flex items-baseline gap-6 mb-2">
        <div>
          <p className="text-xs uppercase text-gray-500 font-bold">Raised</p>
          <p className="text-3xl font-black" style={{ color: primary }}>{currencySym}{(stats.total_raised || 0).toFixed(2)}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-gray-500 font-bold">Entries</p>
          {/* paid_entries (not total_entries) so the count always matches
              Raised. total_entries includes pending/abandoned checkout rows
              which haven't actually contributed any money. */}
          <p className="text-3xl font-black text-gray-900">{stats.paid_entries ?? stats.total_entries ?? 0}</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-5 leading-relaxed" data-testid="raised-net-disclaimer">
        {netNote}
      </p>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-6 text-center border-t border-gray-100">No entries yet — share your link to start collecting.</p>
      ) : (
        <div className="border-t border-gray-100 overflow-x-auto">
          <table className="w-full text-sm" data-testid="entries-table">
            <thead>
              <tr className="text-xs uppercase text-gray-500 text-left">
                <th className="py-3 pr-3">Name</th>
                <th className="py-3 pr-3">Email</th>
                <th className="py-3 pr-3">Picks</th>
                <th className="py-3 text-right">Amount</th>
                {isSuperAdmin && <th className="py-3 pl-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e.id || i} className="border-t border-gray-100" data-testid={`entry-row-${i}`}>
                  <td className="py-2.5 pr-3 font-medium text-gray-900">{e.player_name}</td>
                  <td className="py-2.5 pr-3 text-gray-600">{e.player_email}</td>
                  <td className="py-2.5 pr-3 text-gray-600">{(e.team_names || []).join(', ') || '—'}</td>
                  <td className="py-2.5 text-right font-medium text-gray-900">{currencySym}{Number(e.amount_paid ?? e.amount ?? 0).toFixed(2)}</td>
                  {isSuperAdmin && (
                    <td className="py-2.5 pl-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => resendConfirmation(e, false)}
                        disabled={busyId === e.id}
                        className="px-2 py-1 text-xs font-semibold border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        data-testid={`resend-confirmation-btn-${i}`}
                        title="Re-send the entry confirmation email (skips if already logged)"
                      >
                        {busyId === e.id ? '…' : 'Resend'}
                      </button>
                      <button
                        onClick={() => resendConfirmation(e, true)}
                        disabled={busyId === e.id}
                        className="ml-1.5 px-2 py-1 text-xs font-semibold bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
                        data-testid={`force-resend-confirmation-btn-${i}`}
                        title="Force re-send even if an email_log row already exists (use when player says it landed in spam)"
                      >
                        Force
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {isSuperAdmin && resendMsg && (
            <div className={`mt-3 text-xs ${resendMsg.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="entries-resend-msg">
              {resendMsg.text}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PrizesCard({ slug, initial, primary, authHeaders, onSaved }) {
  const [prizes, setPrizes] = useState(initial.map(p => ({
    name: p.name || p.title || '',
    description: p.description || '',
    sponsor: p.sponsor || p.sponsor_name || '',
    sponsor_logo_url: p.sponsor_logo_url || '',
  })));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const updatePrize = (idx, patch) => {
    const c = [...prizes];
    c[idx] = { ...c[idx], ...patch };
    setPrizes(c);
  };

  const uploadLogo = async (idx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setMsg('Max 5MB per image');
      setTimeout(() => setMsg(''), 2500);
      return;
    }
    setUploadingIdx(idx);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API}/onboard/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      updatePrize(idx, { sponsor_logo_url: r.data.url });
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Upload failed');
      setTimeout(() => setMsg(''), 2500);
    } finally {
      setUploadingIdx(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/clubs/${slug}/prizes`, { prizes: prizes.filter(p => p.name.trim()) }, authHeaders);
      onSaved?.(r.data.prizes || []);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 1500);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const logoSrc = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="prizes-card">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Prizes</p>
        <button onClick={() => setPrizes([...prizes, { name: '', description: '', sponsor: '', sponsor_logo_url: '' }])} className="text-xs font-bold text-[#1D3557] hover:underline flex items-center gap-1" data-testid="add-prize">
          <Plus size={12} /> Add prize
        </button>
      </div>
      <div className="space-y-3 mb-3">
        {prizes.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-3 text-center">No prizes yet.</p>
        ) : prizes.map((p, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-3" data-testid={`prize-row-${idx}`}>
            <div className="flex gap-3 items-start">
              {/* Sponsor logo uploader (left, square) */}
              <label className="relative flex-shrink-0 w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer hover:border-[#1D3557] transition-colors bg-gray-50" title="Upload sponsor logo" data-testid={`prize-logo-label-${idx}`}>
                {p.sponsor_logo_url ? (
                  <img src={logoSrc(p.sponsor_logo_url)} alt="Sponsor logo" className="w-full h-full object-contain p-1" />
                ) : uploadingIdx === idx ? (
                  <div className="text-[10px] text-gray-500">Uploading…</div>
                ) : (
                  <div className="text-center">
                    <div className="text-gray-400 text-xl leading-none">+</div>
                    <div className="text-[9px] text-gray-500 uppercase tracking-wide">Logo</div>
                  </div>
                )}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={e => uploadLogo(idx, e.target.files?.[0])} data-testid={`prize-logo-input-${idx}`} />
                {p.sponsor_logo_url && (
                  <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); updatePrize(idx, { sponsor_logo_url: '' }); }} className="absolute top-0 right-0 w-5 h-5 bg-white/90 rounded-bl text-gray-600 hover:text-red-600 text-xs flex items-center justify-center" aria-label="Remove sponsor logo" data-testid={`prize-logo-remove-${idx}`}>
                    ×
                  </button>
                )}
              </label>
              {/* Name + sponsor fields */}
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={p.name} onChange={e => updatePrize(idx, { name: e.target.value })} placeholder="Prize name" className="px-3 py-2 border border-gray-200 rounded text-sm" data-testid={`prize-name-${idx}`} />
                <input value={p.sponsor} onChange={e => updatePrize(idx, { sponsor: e.target.value })} placeholder="Sponsor (optional)" className="px-3 py-2 border border-gray-200 rounded text-sm" data-testid={`prize-sponsor-${idx}`} />
              </div>
              <button onClick={() => setPrizes(prizes.filter((_, i) => i !== idx))} className="p-2 text-red-400 hover:text-red-600 flex-shrink-0" aria-label="Remove" data-testid={`remove-prize-${idx}`}>
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="px-4 py-2 text-white font-bold text-sm rounded-lg disabled:opacity-50" style={{ background: primary }} data-testid="save-prizes">
          {saving ? 'Saving…' : 'Save prizes'}
        </button>
        {msg && <span className={`text-xs ${msg === 'Saved' ? 'text-green-600' : 'text-red-600'}`}>{msg}</span>}
      </div>
    </section>
  );
}


function GaaInsightsCard({ slug, club, primary, authHeaders }) {
  // Live aggregates over PAID GAA entries: %Kerry / %Mayo / %Draw, avg predicted
  // score, avg first-goal minute, top 5 scorelines. Powers the "Copy for social"
  // caption so the club can drop stats straight into a Facebook / WhatsApp post.
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/gaa/clubs/${slug}/insights`, authHeaders)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Could not load insights'); });
    return () => { cancelled = true; };
  }, [slug]);

  const buildCaption = () => {
    if (!data || data.count === 0) return '';
    const clubName = club?.name || 'The club';
    const totalPicks = data.count;
    // Round the average score for a headline-friendly caption.
    const rk = `${Math.round(data.avg_kerry_goals)}-${String(Math.round(data.avg_kerry_points)).padStart(2, '0')}`;
    const rm = `${Math.round(data.avg_mayo_goals)}-${String(Math.round(data.avg_mayo_points)).padStart(2, '0')}`;
    const avgMinute = data.avg_first_goal_minute != null ? Math.round(data.avg_first_goal_minute) : null;
    const lines = [
      `📣 All-Ireland Final predictions — ${clubName} (${totalPicks} ${totalPicks === 1 ? 'entry' : 'entries'} so far):`,
      '',
      `🏆 ${data.mayo_win_pct}% backing Mayo · ${data.kerry_win_pct}% backing Kerry · ${data.draw_pct}% a draw`,
      `📊 Average predicted score: Kerry ${rk} v Mayo ${rm}`,
    ];
    if (avgMinute != null) lines.push(`⏱️ Average first-goal minute predicted: ${avgMinute}`);
    lines.push('', 'Not entered yet? Get your prediction in before throw-in 👇');
    return lines.join('\n');
  };

  const copyCaption = async () => {
    const text = buildCaption();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback for older Safari
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  if (error) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="gaa-insights-card">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Predictor insights</p>
        <p className="text-sm text-red-600">{error}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="gaa-insights-card">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Predictor insights</p>
        <p className="text-sm text-gray-400 italic">Loading…</p>
      </section>
    );
  }
  if (data.count === 0) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="gaa-insights-card">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Predictor insights</p>
        <p className="text-sm text-gray-500 italic">No paid entries yet — share your predictor to see live insights here.</p>
      </section>
    );
  }

  const rk = `${Math.round(data.avg_kerry_goals)}-${String(Math.round(data.avg_kerry_points)).padStart(2, '0')}`;
  const rm = `${Math.round(data.avg_mayo_goals)}-${String(Math.round(data.avg_mayo_points)).padStart(2, '0')}`;
  const avgMinute = data.avg_first_goal_minute != null ? Math.round(data.avg_first_goal_minute) : null;
  const bar = (pct) => (
    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.max(pct, 2)}%`, background: primary }} />
    </div>
  );

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="gaa-insights-card">
      <div className="flex items-baseline justify-between mb-4">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Predictor insights</p>
        <p className="text-[11px] text-gray-400">{data.count} paid {data.count === 1 ? 'entry' : 'entries'}</p>
      </div>

      {/* Winner splits */}
      <div className="space-y-3 mb-5" data-testid="gaa-insights-splits">
        <div>
          <div className="flex items-baseline justify-between text-sm mb-1">
            <span className="font-semibold text-gray-800">Mayo to win</span>
            <span className="tabular-nums font-black" style={{ color: primary }} data-testid="insight-mayo-pct">{data.mayo_win_pct}% <span className="text-xs font-normal text-gray-400">({data.mayo_win_count})</span></span>
          </div>
          {bar(data.mayo_win_pct)}
        </div>
        <div>
          <div className="flex items-baseline justify-between text-sm mb-1">
            <span className="font-semibold text-gray-800">Kerry to win</span>
            <span className="tabular-nums font-black" style={{ color: primary }} data-testid="insight-kerry-pct">{data.kerry_win_pct}% <span className="text-xs font-normal text-gray-400">({data.kerry_win_count})</span></span>
          </div>
          {bar(data.kerry_win_pct)}
        </div>
        <div>
          <div className="flex items-baseline justify-between text-sm mb-1">
            <span className="font-semibold text-gray-800">Draw</span>
            <span className="tabular-nums font-black" style={{ color: primary }} data-testid="insight-draw-pct">{data.draw_pct}% <span className="text-xs font-normal text-gray-400">({data.draw_count})</span></span>
          </div>
          {bar(data.draw_pct)}
        </div>
      </div>

      {/* Averages */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="insight-avg-score">
          <p className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-1">Average predicted score</p>
          <p className="text-lg font-black tabular-nums text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Kerry {rk} <span className="text-gray-400 font-normal">v</span> Mayo {rm}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">{data.avg_kerry_total.toFixed(1)} pts vs {data.avg_mayo_total.toFixed(1)} pts</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="insight-avg-minute">
          <p className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-1">Average first-goal minute</p>
          <p className="text-lg font-black tabular-nums text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {avgMinute != null ? `min ${avgMinute}` : '—'}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">Tiebreaker</p>
        </div>
      </div>

      {/* Top scorelines */}
      <div className="mb-5" data-testid="insight-top-scorelines">
        <p className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-2">Most-picked scorelines</p>
        <ul className="space-y-1.5">
          {data.top_scorelines.map((row) => (
            <li key={row.scoreline} className="flex items-center justify-between text-sm">
              <span className="text-gray-800 tabular-nums">{row.scoreline}</span>
              <span className="text-xs text-gray-500 tabular-nums">×{row.count} · {row.pct}%</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Copy for social */}
      <div className="pt-4 border-t border-gray-100 flex items-center gap-3">
        <button
          type="button"
          onClick={copyCaption}
          className="px-4 py-2 text-sm font-bold uppercase tracking-widest rounded-lg text-white transition-transform hover:-translate-y-0.5"
          style={{ background: primary }}
          data-testid="copy-social-caption"
        >
          {copied ? 'Copied ✓' : 'Copy for social'}
        </button>
        <p className="text-xs text-gray-500">Paste straight into Facebook / WhatsApp / Instagram.</p>
      </div>
    </section>
  );
}
